// ============================================================================
// BP-04-02: Documents Frontend - API Client
// ============================================================================

import apiClient from '@/core/lib/api-client';
import type {
  Document,
  DocumentCreate,
  DocumentUpdate,
  DocumentFolder,
  DocumentFolderCreate,
  DocumentFolderUpdate,
  DocumentVersion,
  DocumentSearchParams,
  DocumentSearchResult,
  DocumentStats,
  PresignedUploadUrl,
  BulkMoveRequest,
  BulkDeleteRequest,
  BulkDownloadRequest,
  BulkDownloadResult,
  ShareWithAttorneyRequest,
  DocumentShare,
} from '../types/documentsTypes';

// -----------------------------------------------------------------------------
// Base URL
// -----------------------------------------------------------------------------

// Note: apiClient already has /api/v1 as base URL
const DOCUMENTS_BASE = '';

// -----------------------------------------------------------------------------
// Folder API
// -----------------------------------------------------------------------------

export const foldersApi = {
  /**
   * List all folders for a family (returns tree structure)
   */
  list: async (familyId: string): Promise<DocumentFolder[]> => {
    const response = await apiClient.get<DocumentFolder[]>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents/folders`
    );
    return response.data;
  },

  /**
   * Get a single folder with its contents
   */
  get: async (folderId: string): Promise<DocumentFolder> => {
    const response = await apiClient.get<DocumentFolder>(
      `${DOCUMENTS_BASE}/documents/folders/${folderId}`
    );
    return response.data;
  },

  /**
   * Create a new folder
   */
  create: async (familyId: string, data: DocumentFolderCreate): Promise<DocumentFolder> => {
    const response = await apiClient.post<DocumentFolder>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents/folders`,
      data
    );
    return response.data;
  },

  /**
   * Update a folder
   */
  update: async (folderId: string, data: DocumentFolderUpdate): Promise<DocumentFolder> => {
    const response = await apiClient.patch<DocumentFolder>(
      `${DOCUMENTS_BASE}/documents/folders/${folderId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a folder (moves contents to parent)
   */
  delete: async (folderId: string): Promise<void> => {
    await apiClient.delete(`${DOCUMENTS_BASE}/documents/folders/${folderId}`);
  },

  /**
   * Move a folder to a new parent
   */
  move: async (folderId: string, targetParentId: string | null): Promise<DocumentFolder> => {
    const response = await apiClient.post<DocumentFolder>(
      `${DOCUMENTS_BASE}/documents/folders/${folderId}/move`,
      { target_parent_id: targetParentId }
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Document API
// -----------------------------------------------------------------------------

export const documentsApi = {
  /**
   * List documents with filters and pagination
   */
  list: async (familyId: string, params?: DocumentSearchParams): Promise<DocumentSearchResult> => {
    const response = await apiClient.get<DocumentSearchResult>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents`,
      { params }
    );
    return response.data;
  },

  /**
   * Search documents with full-text search
   */
  search: async (familyId: string, params: DocumentSearchParams): Promise<DocumentSearchResult> => {
    const response = await apiClient.get<DocumentSearchResult>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents/search`,
      { params }
    );
    return response.data;
  },

  /**
   * Get a single document by ID
   */
  get: async (documentId: string): Promise<Document> => {
    const response = await apiClient.get<Document>(
      `${DOCUMENTS_BASE}/documents/${documentId}`
    );
    return response.data;
  },

  /**
   * Get presigned upload URL for direct S3 upload
   */
  getUploadUrl: async (
    familyId: string,
    data: DocumentCreate & { filename: string; content_type: string }
  ): Promise<PresignedUploadUrl> => {
    const response = await apiClient.post<PresignedUploadUrl>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents/presigned-url`,
      data
    );
    return response.data;
  },

  /**
   * Confirm upload completion and create document record
   */
  confirmUpload: async (documentId: string): Promise<Document> => {
    const response = await apiClient.post<Document>(
      `${DOCUMENTS_BASE}/documents/${documentId}/confirm-upload`
    );
    return response.data;
  },

  /**
   * Upload document directly (multipart form data)
   * Alternative to presigned URL upload
   */
  upload: async (
    familyId: string,
    file: File,
    metadata: DocumentCreate,
    onProgress?: (progress: number) => void
  ): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(metadata));

    const response = await apiClient.post<Document>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    return response.data;
  },

  /**
   * Update document metadata
   */
  update: async (documentId: string, data: DocumentUpdate): Promise<Document> => {
    const response = await apiClient.patch<Document>(
      `${DOCUMENTS_BASE}/documents/${documentId}`,
      data
    );
    return response.data;
  },

  /**
   * Soft delete a document
   */
  delete: async (documentId: string): Promise<void> => {
    await apiClient.delete(`${DOCUMENTS_BASE}/documents/${documentId}`);
  },

  /**
   * Get presigned download URL
   */
  getDownloadUrl: async (documentId: string): Promise<{ url: string; expires_at: string }> => {
    const response = await apiClient.get<{ url: string; expires_at: string }>(
      `${DOCUMENTS_BASE}/documents/${documentId}/download`
    );
    return response.data;
  },

  /**
   * Get preview URL (for images and PDFs)
   */
  getPreviewUrl: async (documentId: string): Promise<{ url: string; expires_at: string }> => {
    const response = await apiClient.get<{ url: string; expires_at: string }>(
      `${DOCUMENTS_BASE}/documents/${documentId}/preview`
    );
    return response.data;
  },

  /**
   * Move document to different folder
   */
  move: async (documentId: string, targetFolderId: string | null): Promise<Document> => {
    const response = await apiClient.post<Document>(
      `${DOCUMENTS_BASE}/documents/${documentId}/move`,
      { target_folder_id: targetFolderId }
    );
    return response.data;
  },

  /**
   * Copy document
   */
  copy: async (documentId: string, targetFolderId: string | null): Promise<Document> => {
    const response = await apiClient.post<Document>(
      `${DOCUMENTS_BASE}/documents/${documentId}/copy`,
      { target_folder_id: targetFolderId }
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Version API
// -----------------------------------------------------------------------------

export const versionsApi = {
  /**
   * Get version history for a document
   */
  list: async (documentId: string): Promise<DocumentVersion[]> => {
    const response = await apiClient.get<DocumentVersion[]>(
      `${DOCUMENTS_BASE}/documents/${documentId}/versions`
    );
    return response.data;
  },

  /**
   * Upload a new version
   */
  upload: async (
    documentId: string,
    file: File,
    changeNotes?: string,
    onProgress?: (progress: number) => void
  ): Promise<DocumentVersion> => {
    const formData = new FormData();
    formData.append('file', file);
    if (changeNotes) {
      formData.append('change_notes', changeNotes);
    }

    const response = await apiClient.post<DocumentVersion>(
      `${DOCUMENTS_BASE}/documents/${documentId}/versions`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    return response.data;
  },

  /**
   * Restore a previous version
   */
  restore: async (documentId: string, versionId: string): Promise<Document> => {
    const response = await apiClient.post<Document>(
      `${DOCUMENTS_BASE}/documents/${documentId}/versions/${versionId}/restore`
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Sharing API
// -----------------------------------------------------------------------------

export const sharingApi = {
  /**
   * Share document with attorney
   */
  shareWithAttorney: async (
    documentId: string,
    data: ShareWithAttorneyRequest
  ): Promise<DocumentShare> => {
    const response = await apiClient.post<DocumentShare>(
      `${DOCUMENTS_BASE}/documents/${documentId}/share-attorney`,
      data
    );
    return response.data;
  },

  /**
   * Revoke attorney access
   */
  revokeAttorneyAccess: async (documentId: string, attorneyId: string): Promise<void> => {
    await apiClient.delete(
      `${DOCUMENTS_BASE}/documents/${documentId}/share-attorney/${attorneyId}`
    );
  },

  /**
   * List all shares for a document
   */
  listShares: async (documentId: string): Promise<DocumentShare[]> => {
    const response = await apiClient.get<DocumentShare[]>(
      `${DOCUMENTS_BASE}/documents/${documentId}/shares`
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Bulk Operations API
// -----------------------------------------------------------------------------

export const bulkApi = {
  /**
   * Move multiple documents to a folder
   */
  move: async (familyId: string, data: BulkMoveRequest): Promise<Document[]> => {
    const response = await apiClient.post<Document[]>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents/bulk-move`,
      data
    );
    return response.data;
  },

  /**
   * Delete multiple documents
   */
  delete: async (familyId: string, data: BulkDeleteRequest): Promise<{ deleted_count: number }> => {
    const response = await apiClient.post<{ deleted_count: number }>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents/bulk-delete`,
      data
    );
    return response.data;
  },

  /**
   * Download multiple documents as ZIP
   */
  download: async (familyId: string, data: BulkDownloadRequest): Promise<BulkDownloadResult> => {
    const response = await apiClient.post<BulkDownloadResult>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents/bulk-download`,
      data
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Statistics API
// -----------------------------------------------------------------------------

export const statsApi = {
  /**
   * Get document statistics for a family
   */
  get: async (familyId: string): Promise<DocumentStats> => {
    const response = await apiClient.get<DocumentStats>(
      `${DOCUMENTS_BASE}/families/${familyId}/documents/stats`
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Combined Export
// -----------------------------------------------------------------------------

export const documentApiClient = {
  folders: foldersApi,
  documents: documentsApi,
  versions: versionsApi,
  sharing: sharingApi,
  bulk: bulkApi,
  stats: statsApi,
};

export default documentApiClient;


